'use client';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCombinedRefs, useComponentDeprecation, useDeviceDetect, useDebouncedCallback } from '../../util/';
import { DynamicGrid } from '../DynamicGrid';
import { ButtonContainer, CarouselButton, Container, SpotlightIndicator, CarouselAriaContainer } from './elements';
import { getVariantOptions, getVariantOverrides } from './variantConfig';
/**
 * @deprecated
 */
export const Carousel = /*#__PURE__*/forwardRef(({
  options = {},
  elementRole = 'list',
  variant = 'default',
  edgeItemsPadding = 'unset',
  showArrowOnDesktop = false,
  showArrowOnMobile = true,
  handleCurrentDotIndexChange,
  children,
  ...rest
}, fRef) => {
  useComponentDeprecation({
    name: 'Carousel',
    version: '5'
  });
  const innerRef = useRef(document.createElement('div'));
  const innerAriaRef = useRef(document.createElement('div'));
  const combinedRef = useCombinedRefs(fRef, innerRef);
  const [leftButtonDisabled, setLeftButtonDisabled] = useState(false);
  const [rightButtonDisabled, setRightButtonDisabled] = useState(false);
  const [currentDotIndex, setCurrentDotIndex] = useState(0);
  const [totalDots, setTotalDots] = useState(0);
  const [hintWidth, setHintWidth] = useState(0);
  const childLength = innerRef.current.children.length;
  useEffect(() => {
    handleCurrentDotIndexChange === null || handleCurrentDotIndexChange === void 0 ? void 0 : handleCurrentDotIndexChange(currentDotIndex);
  }, [currentDotIndex]);
  const variantOptions = useMemo(() => getVariantOptions(variant, options), [variant, options]);
  const variantOverrides = useMemo(() => getVariantOverrides(variant), [variant]);
  const getHintWidth = useCallback(() => {
    var _innerRef$current, _innerRef$current$fir;
    return setHintWidth(((_innerRef$current = innerRef.current) === null || _innerRef$current === void 0 ? void 0 : (_innerRef$current$fir = _innerRef$current.firstElementChild) === null || _innerRef$current$fir === void 0 ? void 0 : _innerRef$current$fir.clientWidth) * variantOptions.scrollHint);
  }, [innerRef.current, variantOptions.scrollHint]);
  const getTotalItems = useCallback(() => {
    setTotalDots(React.Children.count(children));
  }, []);
  const getCurrentDotIndex = useCallback((itemNumber, scrollWidth, scrollLeft, rightButtonDisabled) => {
    const scrollPercent = scrollLeft / scrollWidth;
    const dotIndex = rightButtonDisabled ? totalDots - 1 : Math.round(scrollPercent * itemNumber);
    setCurrentDotIndex(dotIndex);
  }, [totalDots]);
  const scrollCallback = useCallback(() => {
    const scrollBuffer = 20;
    const {
      scrollLeft,
      clientWidth,
      scrollWidth
    } = innerRef.current;
    requestAnimationFrame(() => {
      setLeftButtonDisabled(scrollLeft <= scrollBuffer);
      setRightButtonDisabled(scrollLeft + clientWidth >= scrollWidth - scrollBuffer);
    });
    getTotalItems();
    getCurrentDotIndex(totalDots, scrollWidth, scrollLeft, rightButtonDisabled);
  }, [innerRef.current, variantOptions.scrollHint, totalDots, rightButtonDisabled]);
  useEffect(() => {
    scrollCallback();
    getHintWidth();
    if (variantOptions.scrollInterval && !rightButtonDisabled) {
      const intervalHandle = setInterval(() => {
        handleScroll('right');
      }, variantOptions.scrollInterval);
      return () => clearInterval(intervalHandle);
    }
  }, [variantOptions, rightButtonDisabled, scrollCallback, getHintWidth]);
  const handleScroll = useCallback(scrollDirection => {
    toggleAriaStyles(true);
    const direction = scrollDirection === 'left' ? -1 : 1;
    const {
      clientWidth
    } = innerRef.current.firstElementChild;
    const {
      offsetWidth
    } = innerRef.current;
    const step = variantOptions.scrollStep === 'increment' ? clientWidth : offsetWidth - hintWidth;
    innerRef.current.scrollBy({
      left: step * direction
    });
  }, [innerRef.current, variantOptions.scrollStep, variantOptions.scrollHint]);
  const handleResize = useDebouncedCallback(() => {
    scrollCallback();
  }, [scrollCallback], 200);
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);
  const handleCarouselIndicatorClick = useCallback((clickedIndicatorIndex = 0) => {
    const difference = clickedIndicatorIndex - currentDotIndex;
    toggleAriaStyles(true);
    const {
      clientWidth
    } = innerRef.current.firstElementChild;
    const {
      offsetWidth
    } = innerRef.current;
    const step = variantOptions.scrollStep === 'increment' ? clientWidth : offsetWidth - hintWidth;
    innerRef.current.scrollBy({
      left: step * difference
    });
  }, [innerRef.current, variantOptions.scrollStep, variantOptions.scrollHint, currentDotIndex]);
  const toggleAriaStyles = useCallback(visibility => {
    innerAriaRef.current.style.visibility = visibility ? 'visible' : 'hidden';
  }, []);
  const {
    isMobile
  } = useDeviceDetect();
  return /*#__PURE__*/React.createElement(Container, {
    "data-aui": "carousel",
    $edgeItemsPadding: edgeItemsPadding
  }, childLength > 1 && /*#__PURE__*/React.createElement(ButtonContainer, {
    suggestiveFade: variantOptions.suggestiveFade,
    disabled: leftButtonDisabled,
    direction: "left"
  }, /*#__PURE__*/React.createElement(CarouselButton, {
    "aria-label": "Previous items",
    "aria-hidden": leftButtonDisabled,
    onClick: () => handleScroll('left'),
    iconSrc: "arrow-left",
    tabIndex: 0,
    disabled: leftButtonDisabled,
    showArrowOnDesktop: showArrowOnDesktop,
    showArrowOnMobile: showArrowOnMobile,
    isMobile: isMobile
  })), /*#__PURE__*/React.createElement(DynamicGrid, _extends({
    ref: combinedRef,
    scroll: variantOptions,
    elementRole: elementRole,
    onScroll: scrollCallback,
    className: 'scroll-grid'
  }, rest, variantOverrides, {
    onFocus: () => toggleAriaStyles(false)
  }), children), childLength > 1 && /*#__PURE__*/React.createElement(ButtonContainer, {
    suggestiveFade: variantOptions.suggestiveFade,
    disabled: rightButtonDisabled,
    direction: "right"
  }, /*#__PURE__*/React.createElement(CarouselButton, {
    "aria-label": "Next items",
    "aria-hidden": rightButtonDisabled,
    onClick: () => handleScroll('right'),
    iconSrc: "arrow-right",
    tabIndex: 0,
    disabled: rightButtonDisabled,
    showArrowOnDesktop: showArrowOnDesktop,
    showArrowOnMobile: showArrowOnMobile,
    isMobile: isMobile
  })), variant === 'spotlight' && childLength > 1 && /*#__PURE__*/React.createElement(SpotlightIndicator, {
    currentIndex: currentDotIndex,
    totalItems: totalDots,
    handleCarouselIndicatorClick: handleCarouselIndicatorClick
  }), /*#__PURE__*/React.createElement(CarouselAriaContainer, {
    "aria-relevant": "all",
    role: "alert",
    ref: innerAriaRef
  }, "Item ", currentDotIndex, " of ", totalDots - 1, " total"));
});